import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { MessageSquare, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { FeedbackRequest, FeedbackResponse, Product } from '@/types/feedback';
import { fetchProducts } from '@/constants/products';
import { submitFeedback } from '@/utils/feedbackApi';

interface FeedbackFormProps {
  onSubmissionSuccess: (request: FeedbackRequest, response: FeedbackResponse) => void;
  jwtToken?: string;
  productInfo?: { productId: string; productName: string } | null;
}

export const FeedbackForm = ({ onSubmissionSuccess, jwtToken, productInfo }: FeedbackFormProps) => {
  const [content, setContent] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!productInfo?.productId || !content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide feedback content.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody: FeedbackRequest = {
        productId: productInfo.productId,
        content: content.trim()
      };

      const response = await submitFeedback(requestBody, jwtToken);
      
      onSubmissionSuccess(requestBody, response);
      
      toast({
        title: "Feedback Submitted!",
        description: "Thank you for your valuable feedback. We'll review it soon.",
      });
      
      // Reset form
      setContent('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-border/70 bg-card/90 backdrop-blur-sm">
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-lg text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-2xl border-border/70 bg-card/90 backdrop-blur-sm">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-r from-primary via-brand-coral to-brand-amber rounded-full flex items-center justify-center">
            <MessageSquare className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-3xl font-bold text-primary">
            Product Feedback
          </CardTitle>
          <CardDescription className="text-lg text-muted-foreground">
            Help us improve by sharing your thoughts and experiences
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="product" className="text-sm font-medium text-foreground">
                Product
              </Label>
              <div className="w-full h-12 border border-border rounded-md bg-secondary/50 flex items-center px-3 text-foreground">
                {productInfo?.productName || 'Loading product...'}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback" className="text-sm font-medium text-foreground">
                Your Feedback
              </Label>
              <Textarea
                id="feedback"
                placeholder="Tell us about your experience, suggestions for improvement, or any issues you've encountered..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-32 resize-none border-border focus:border-primary focus:ring-primary"
                required
              />
              <p className="text-xs text-muted-foreground">
                Be specific and constructive. Your feedback helps us build better products.
              </p>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !productInfo?.productId || !content.trim()}
              className="w-full h-12 bg-gradient-to-r from-primary via-brand-coral to-brand-amber hover:from-primary/90 hover:via-brand-coral/90 hover:to-brand-amber/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-medium transition-all duration-200 transform hover:scale-[1.02]"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Submit Feedback
                </div>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
